import React, { useState, useEffect, useRef } from 'react';
import './index.css';
import HomePage from './HomePage';
import SectionPage from './SectionPage';
import PostPage from './PostPage';
import AboutPage from './AboutPage';
import PrivacyPage from './PrivacyPage';
import { SECTIONS, getSection, getPost } from './content';

const ABOUT_MENU = [
  { key: 'about',   label: 'About the Station' },
  { key: 'privacy', label: 'Privacy Policy'    },
];

const ABOUT_MENU_KEYS = ABOUT_MENU.map((item) => item.key);

const NAV_ITEMS = [
  { key: 'home', label: 'Home' },
  ...SECTIONS.map((s) => ({ key: s.key, label: s.label })),
  { key: 'about', label: 'About', menu: ABOUT_MENU },
];

const SECTION_KEYS = SECTIONS.map((s) => s.key);
const VALID_PAGES = ['home', ...SECTION_KEYS, 'about', 'privacy'];

/**
 * Routes are hash based so the site stays a plain static bundle with no
 * server rewrites, matching the churchvalley site and the Caddy config.
 *   #/            home
 *   #/equipment   a section listing
 *   #/post/<slug> a single article
 */
const routeFromHash = () => {
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (hash.startsWith('post/')) {
    const slug = hash.slice('post/'.length);
    return getPost(slug) ? { page: 'post', slug } : { page: 'home' };
  }
  return { page: VALID_PAGES.includes(hash) ? hash : 'home' };
};

function PageView({ route, navigate }) {
  switch (route.page) {
    case 'post':    return <PostPage slug={route.slug} navigate={navigate} />;
    case 'about':   return <AboutPage navigate={navigate} />;
    case 'privacy': return <PrivacyPage />;
    default: {
      const section = getSection(route.page);
      return section
        ? <SectionPage section={section} navigate={navigate} />
        : <HomePage navigate={navigate} />;
    }
  }
}

function NavDropdown({ item, route, navigate }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const isActive = ABOUT_MENU_KEYS.includes(route.page);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (key) => {
    setOpen(false);
    navigate(key);
  };

  return (
    <div className="nav-dropdown" ref={wrapperRef}>
      <button
        className={isActive ? 'active' : ''}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {item.label}
        <span className="nav-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="nav-dropdown-menu" role="menu">
          {item.menu.map(({ key, label }) => (
            <li key={key} role="none">
              <button
                role="menuitem"
                className={route.page === key ? 'active' : ''}
                onClick={() => handleSelect(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function App() {
  const [route, setRoute] = useState(routeFromHash);

  useEffect(() => {
    const onHashChange = () => {
      setRoute(routeFromHash());
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigate = (key, slug) => {
    const target = key === 'post' ? `post/${slug}` : key === 'home' ? '' : key;
    if (window.location.hash.replace(/^#\/?/, '') === target) return;
    window.location.hash = target;
  };

  return (
    <>
      <div className="page-bg" aria-hidden="true" />

      <div className="app-shell">
        {/* ── Navigation ── */}
        <nav className="nav">
          <button
            className="nav-logo"
            onClick={() => navigate('home')}
            aria-label="NK4I home"
          >
            <span className="nav-logo-call">NK4I</span>
            <span className="nav-logo-sub">Amateur Radio</span>
          </button>

          <ul className="nav-links">
            {NAV_ITEMS.map((item) => (
              <li key={item.key}>
                {item.menu ? (
                  <NavDropdown item={item} route={route} navigate={navigate} />
                ) : (
                  <button
                    className={route.page === item.key ? 'active' : ''}
                    onClick={() => navigate(item.key)}
                    aria-current={route.page === item.key ? 'page' : undefined}
                  >
                    {item.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Key forces a re-mount, and so re-animation, on navigation */}
        <main key={`${route.page}/${route.slug || ''}`}>
          <PageView route={route} navigate={navigate} />
        </main>

        {/* ── Footer ── */}
        <footer className="footer">
          <span className="footer-logo">NK4I</span>
          <span className="footer-copy">
            © {new Date().getFullYear()} NK4I · Tighe Kuykendall
          </span>
          <button className="footer-link" onClick={() => navigate('about')}>
            About
          </button>
          <button className="footer-link" onClick={() => navigate('privacy')}>
            Privacy Policy
          </button>
        </footer>
      </div>
    </>
  );
}
